import React, { useState } from "react";

import { useTranslation } from "react-i18next";
import { omit, isEqual } from "lodash";

import IconPicker from "../../../../../../components/ui/icon-picker/IconPicker";
import AddToLibraryButton from "../../../../../../components/ui/add-to-library-button/AddToLibraryButton";
import ButtonGroup from "../../../../../../components/ui/button-group/ButtonGroup";
import ImagePicker from "../../../../../../components/ui/image-picker/ImagePicker";
import IllustrationPicker from "../../../../../../components/ui/illustration-picker/IllustrationPicker";
import Text from "../../../../../../components/ui/input/text/Input";
import LinkPicker from "../../../../../../components/ui/link-picker/LinkPicker";
import GoBack from "../../components/GoBack";
import ColorPicker from "../../../../../../components/ui/color-picker/ColorPicker";
import TextArea from "../../../../../../components/ui/input/text-area/TextArea";
import { cardsLibraryService } from "@Shared/Service/CardsLibraryService";
import { useCardsStore } from "../../../../../../stores/use-cards-store";
import { useOpticsStore } from "../../../../../../stores/use-optics-store";
import {
  OptionContainer,
  OptionContent,
  OptionLabel,
  SettingsOptionContainer,
  SettingsOptionHeading,
} from "../../styles";
import {
  CARDS_PROP,
  EMOJI_TEMPLATE_TABS,
  LIBRARY_SERVICE_ACTION,
} from "./defaults";
import { BOOLEAN_OPTIONS } from "./options";

const CardOptions = () => {
  const [isRequestingToLibrary, setIsRequestingToLibrary] = useState(false);

  const { cardData, updateCardData, setCard } = useCardsStore((state) => ({
    cardData: state.card,
    updateCardData: state.updateCardData,
    setCard: state.setCard,
  }));

  const { t } = useTranslation();

  const { card, config } = cardData;

  const { libraryCards, setLibraryCards, hasStyleguidePermission } =
    useOpticsStore((state) => ({
      libraryCards: state.libraryCards,
      setLibraryCards: state.setLibraryCards,
      hasStyleguidePermission: state.hasStyleguidePermission,
    }));

  const selectedLibraryCard = libraryCards.find(
    (libraryCard) => libraryCard.cardData.id === card.id
  );

  const handleCardLibraryRequest = async (fnName) => {
    try {
      setIsRequestingToLibrary(true);
      const response = await cardsLibraryService[fnName](
        card.id,
        cardData.config,
        card,
        cardData.tabs || []
      );
      if (response?.data?.cardId) {
        setLibraryCards([
          ...libraryCards,
          { cardData: card, config: cardData.config, tabs: cardData.tabs },
        ]);
      } else {
        setLibraryCards([
          ...libraryCards.map((crd) => {
            if (crd.cardData.id === card.id) {
              return {
                ...crd,
                cardData: card,
                config: cardData.config,
                tabs: cardData.tabs,
              };
            }
            return crd;
          }),
        ]);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsRequestingToLibrary(false);
    }
  };

  const isEmoji = typeof card.icon === "object";

  return (
    <>
      <GoBack
        onClick={() => setCard(undefined)}
        content={t("selected-card-content")}
      />
      {config.type === "image" && (
        <SettingsOptionContainer>
          <SettingsOptionHeading>{t("image")}</SettingsOptionHeading>
          <ImagePicker
            hasImageReposition
            image={card[CARDS_PROP.IMAGE]}
            onChange={(value) =>
              updateCardData({
                id: card.id,
                key: CARDS_PROP.IMAGE,
                value,
              })
            }
          />
        </SettingsOptionContainer>
      )}

      {config.type === "illustration" && (
        <SettingsOptionContainer>
          <SettingsOptionHeading>{t("illustration")}</SettingsOptionHeading>
          <OptionContainer>
            <OptionContent>
              <IllustrationPicker
                illustration={card[CARDS_PROP.IMAGE]}
                onChange={(value) =>
                  updateCardData({
                    id: card.id,
                    key: CARDS_PROP.IMAGE,
                    value,
                  })
                }
              />
            </OptionContent>
          </OptionContainer>
        </SettingsOptionContainer>
      )}

      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("link")}</SettingsOptionHeading>
        <OptionContainer>
          <OptionLabel alignToTop>{t("link-to")}</OptionLabel>
          <OptionContent>
            <LinkPicker
              value={card[CARDS_PROP.LINK]}
              onChange={(value) =>
                updateCardData({
                  id: card.id,
                  key: CARDS_PROP.LINK,
                  value,
                })
              }
            />
          </OptionContent>
        </OptionContainer>
        <OptionContainer>
          <OptionLabel>{t("new-tab")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={BOOLEAN_OPTIONS}
              value={card[CARDS_PROP.NEW_TAB] === "true"}
              onClick={(isOn) =>
                updateCardData({
                  id: card.id,
                  key: CARDS_PROP.NEW_TAB,
                  value: isOn ? "true" : "false",
                })
              }
            />
          </OptionContent>
        </OptionContainer>
      </SettingsOptionContainer>
      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("content")}</SettingsOptionHeading>
        {config.type === "emoji" && (
          <OptionContainer>
            <OptionLabel>{t("emoji")}</OptionLabel>
            <OptionContent>
              <IconPicker
                defaultTab={isEmoji ? "Emojis" : "Icons"}
                tabs={EMOJI_TEMPLATE_TABS[config.design]}
                icon={card[CARDS_PROP.ICON]}
                onChange={({ value }) =>
                  updateCardData({
                    id: card.id,
                    key: CARDS_PROP.ICON,
                    value,
                  })
                }
              />
            </OptionContent>
          </OptionContainer>
        )}

        {config.layout.includes(CARDS_PROP.TITLE) && (
          <OptionContainer>
            <OptionLabel>{t("title")}</OptionLabel>
            <OptionContent>
              <Text
                placeholder="Add..."
                value={card[CARDS_PROP.TITLE]}
                onChange={(value) =>
                  updateCardData({
                    id: card.id,
                    key: CARDS_PROP.TITLE,
                    value,
                  })
                }
              />
            </OptionContent>
          </OptionContainer>
        )}

        {config.layout.includes(CARDS_PROP.TEXT) && (
          <OptionContainer>
            <OptionLabel>{t("subtext")}</OptionLabel>
            <OptionContent>
              <TextArea
                placeholder="Add..."
                value={card[CARDS_PROP.TEXT]}
                onChange={({ target: { value } }) =>
                  updateCardData({
                    id: card.id,
                    key: CARDS_PROP.TEXT,
                    value,
                  })
                }
              />
            </OptionContent>
          </OptionContainer>
        )}

        {config.layout.includes(CARDS_PROP.LINK_TEXT) && (
          <OptionContainer>
            <OptionLabel>{t("link-text")}</OptionLabel>
            <OptionContent>
              <Text
                placeholder="Add..."
                value={card[CARDS_PROP.LINK_TEXT]}
                onChange={(value) =>
                  updateCardData({
                    id: card.id,
                    key: CARDS_PROP.LINK_TEXT,
                    value,
                  })
                }
              />
            </OptionContent>
          </OptionContainer>
        )}

        {config.layout.includes(CARDS_PROP.LABEL) && (
          <OptionContainer>
            <OptionLabel>{t("label")}</OptionLabel>
            <OptionContent>
              <Text
                placeholder="Add..."
                value={card[CARDS_PROP.LABEL]}
                onChange={(value) =>
                  updateCardData({
                    id: card.id,
                    key: CARDS_PROP.LABEL,
                    value,
                  })
                }
              />
            </OptionContent>
          </OptionContainer>
        )}
      </SettingsOptionContainer>

      {config.layout.includes(CARDS_PROP.LABEL) && (
        <SettingsOptionContainer>
          <SettingsOptionHeading>{t("styling-options")}</SettingsOptionHeading>
          <OptionContainer>
            <OptionLabel alignToTop>{t("colors")}</OptionLabel>
            <OptionContent>
              <ColorPicker
                value={card[CARDS_PROP.COLORS]}
                onChange={(value) =>
                  updateCardData({ id: card.id, key: CARDS_PROP.COLORS, value })
                }
              />
            </OptionContent>
          </OptionContainer>
        </SettingsOptionContainer>
      )}

      {hasStyleguidePermission && (
        <SettingsOptionContainer style={{ border: "none", marginTop: "auto" }}>
          <OptionContainer>
            <AddToLibraryButton
              onClick={(action) =>
                handleCardLibraryRequest(LIBRARY_SERVICE_ACTION[action])
              }
              isSaved={!!selectedLibraryCard}
              isLoading={isRequestingToLibrary}
              hasChanges={
                selectedLibraryCard
                  ? !isEqual(
                      omit(selectedLibraryCard.cardData, ["color"]),
                      omit(card, ["color"])
                    ) || !isEqual(selectedLibraryCard.config, cardData.config)
                  : false
              }
              macroName="card"
            />
          </OptionContainer>
        </SettingsOptionContainer>
      )}
    </>
  );
};

export default CardOptions;
